import { t } from 'i18next';
import { isBrowser } from './browserHelper';
import {
  COOKIE_TO_IKEAAPP,
  getCookieAsObject,
  setCookie,
} from './cookieHelper';
import { colors } from '../styles';

export type NativeAppParsedQueryParams = {
  ikeaTheme: string;
  testMode: string;
  redirectUrl: string;
  platform: string;
};

export type NativeAppQueryParamKeys =
  | 'ikea_theme'
  | 'test_mode'
  | 'redirect_url'
  | 'platform';

const NATIVE_APP_QUERY_PARAMS = {
  IKEA_THEME: 'ikea_theme',
  TEST_MODE: 'test_mode',
  REDIRECT_URL: 'redirect_url',
  PLATFORM: 'platform',
};

const DEFAULT_NATIVE_APP_PARSED_QUERY_PARAMS = {
  ikeaTheme: '',
  testMode: '',
  redirectUrl: '',
  platform: '',
};

const NATIVE_APP_DARK_THEME_KEY_VALUE = 'ikea_theme=dark';
const NATIVE_APP_DARK_THEME_ATTRIBUTE = 'ikea-theme-dark';
/* ikeaapp cleanup: Remove NATIVE_APP_OPERATION in all places once the app team starts using /app instead of /ikeaapp 
Also, remove 'ikeaapp' from the operations in the country configs, then run the Github action 'Delete operation on host' */
export const NATIVE_APP_OPERATION = 'ikeaapp';
export const NATIVE_APP = 'app';

export const setNativeAppTheme = () => {
  // This could be improved by moving the logic to set the attribute from an
  // inline script directly in the static html file. The current solution
  // applies dark mode after the javascript has loaded. Using an inline script
  // in the html file would apply dark mode directly after the html has loaded.
  if (!isBrowser()) return;
  if (
    isNativeApp() &&
    window.location.search.includes(NATIVE_APP_DARK_THEME_KEY_VALUE)
  ) {
    document.body.setAttribute(NATIVE_APP_DARK_THEME_ATTRIBUTE, '');
    // This is added for iOS, so they inherit correct overflow color.
    document.body.style.backgroundColor = colors.background.default;
  } else {
    document.body.removeAttribute(NATIVE_APP_DARK_THEME_ATTRIBUTE);
  }
};

/* ikeaapp cleanup: Remove function once the app team starts using /app instead of /ikeaapp */
export const isNativeAppOperation = () => {
  return isBrowser() && window.location.href.includes(NATIVE_APP_OPERATION);
};

export const isNativeAppView = (): boolean => {
  return (
    isBrowser() &&
    window.location.pathname
      .split('/')
      .some((urlPart) => urlPart === NATIVE_APP)
  );
};

export const isNativeApp = () => {
  return isNativeAppOperation() || isNativeAppView();
};

export const getNativeAppQueryParams = (): NativeAppParsedQueryParams => {
  if (isNativeApp()) {
    const queryParams = new URLSearchParams(window.location.search);
    const { IKEA_THEME, TEST_MODE, REDIRECT_URL, PLATFORM } =
      NATIVE_APP_QUERY_PARAMS;
    const ikeaTheme = queryParams.get(IKEA_THEME) || '';
    const testMode = queryParams.get(TEST_MODE) || '';
    const redirectUrl = queryParams.get(REDIRECT_URL) || '';
    const platform = queryParams.get(PLATFORM) || '';
    return { ikeaTheme, testMode, redirectUrl, platform };
  }
  return DEFAULT_NATIVE_APP_PARSED_QUERY_PARAMS;
};

export const getNativeAppQueryParamNames = () => {
  return isNativeApp() ? Object.values(NATIVE_APP_QUERY_PARAMS) : [];
};

const getOperationAllowedRedirects = (operation: string): string[] => {
  const links: string[] = [];
  const tocDefaultUrl = t(`pages.contact.termsAndConditions.link.default`);
  const tocOperationsUrl = t(
    `pages.contact.termsAndConditions.link.${operation}`,
  );

  if (tocDefaultUrl.includes('http'))
    links.push(encodeURIComponent(tocDefaultUrl));
  if (tocOperationsUrl.includes('http'))
    links.push(encodeURIComponent(tocOperationsUrl));

  return links;
};

export const setCookieForNativeApp = (operation: string) => {
  if (isNativeApp()) {
    setCookie(COOKIE_TO_IKEAAPP, {
      allowedRedirects: getOperationAllowedRedirects(operation),
    });
  }
};

export const appendNewCookieValueForNativeApp = (cookieNewValue: object) => {
  const value = {
    ...getCookieAsObject(COOKIE_TO_IKEAAPP),
    ...cookieNewValue,
  };
  if (isNativeApp()) {
    setCookie(COOKIE_TO_IKEAAPP, value);
  }
};
